import { Box, Button, Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { delChargeData, getChargeCategoryList, getChargeList } from "../../Api/Store";
import { ChargeCategory } from "../../Objects/ChargeCategory";
import { ChargePattern } from "../../Objects/ChargePattern";
import ChargePatternCard from "../molecules/ChargePatternCard";
import ChargePatternEditDialog from "./ChargePatternEditDialog";

export interface StudioPriceListProps {
    str_id: number
    std_id: number
}

const StudioPriceList: React.FC<StudioPriceListProps> = ({
    str_id,
    std_id,
}) => {
    let { enqueueSnackbar } = useSnackbar()
    const [chargePatternList, setChargePatternList] = React.useState<ChargePattern[]>()
    const [chargeCategoryList, setChargeCategoryList] = React.useState<ChargeCategory[]>()
    const [editChrptnId, setEditChrptnId] = React.useState<number>()
    const [editChrptnDialog, setEditChrptnDialog] = React.useState<boolean>(false)

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadChargeList = React.useCallback(() => {
        getChargeList({
            str_id: str_id,
            std_id: std_id
        }, {signal: abortController.signal}).then(result => {
            setChargePatternList(result.chargePatternList)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, str_id, std_id, enqueueSnackbar])

    const loadChargeCategoryList = React.useCallback(() => {
        getChargeCategoryList({
            "access_key": "HEAD-OFFICE_LOGIN"
        }, {signal: abortController.signal}).then(result => {
            setChargeCategoryList(result.chargeCategoryList)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, enqueueSnackbar])

    const doDelete = React.useCallback((chrptn_id: number) => {
        delChargeData({
            chrptn_id: chrptn_id
        }, {signal: abortController.signal}).then(() => {
            enqueueSnackbar("削除しました", { variant: "success" })
            loadChargeList()
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, loadChargeList, enqueueSnackbar])

    React.useEffect(() => {
        loadChargeList()
        loadChargeCategoryList()
    }, [loadChargeList, loadChargeCategoryList])

    return (
        <Box>
            {chargeCategoryList &&
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setEditChrptnId(undefined)
                                setEditChrptnDialog(true)
                            }}
                        >スタジオ料金設定を追加</Button>
                    </Grid>
                    {chargePatternList?.map(chargePattern => (
                        <Grid item key={chargePattern.chrptn_id} xs={12} sm={12} md={6}>
                            <ChargePatternCard
                                chargePattern={chargePattern}
                                chargeCategoryList={chargeCategoryList}
                                onDelete={() => {
                                    if (chargePattern.chrptn_id) {
                                        doDelete(chargePattern.chrptn_id)
                                    }
                                }}
                                onEdit={() => {
                                    setEditChrptnId(chargePattern.chrptn_id)
                                    setEditChrptnDialog(true)
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>
            }
            <ChargePatternEditDialog
                open={editChrptnDialog}
                chrptnId={editChrptnId}
                onClose={(isEdit) => {
                    if (isEdit) {
                        loadChargeList()
                    }
                    setEditChrptnDialog(false)
                    setEditChrptnId(undefined)
                }}
                str_id={str_id}
                std_id={std_id}
            />
        </Box>
    );
}

export default StudioPriceList